/**
 * VizOverlay - Visualization overlay for Sentinel metadata.
 *
 * Renders detection boxes, crop box, roll indicator, focus zone, and heatmap
 * layers from JSON metadata instead of static viz.jpg images.
 *
 * COORDINATE SYSTEM:
 * - Detection boxes are always in detect-space (2000px width)
 * - Crop boxes may be in detect-space or original-space (see getCropCoordinateSpace)
 * - All coordinates use XYXY format (x1, y1, x2, y2) per CLAUDE.md
 *
 * USAGE:
 * - Controlled component - layers state managed by parent (Edit.jsx)
 * - Use VIZ_PRESETS and VIZ_PRESET_ORDER for preset-based switching
 * - Toggle presets with Alt+V in Edit.jsx
 * - pointerEvents: none - does not interfere with face tagging
 */

import React, { useEffect, useMemo, useRef } from 'react';
import {
  createCoordinateScaler,
  getCropCoordinateSpace,
} from '../../utils/coordinateScaler';

// Default layer visibility
const DEFAULT_LAYERS = {
  detections: true,
  cropBox: true,
  rollIndicator: true,
  focusZone: false,
  heatmap: false,
  labels: true,
  pose: false,
  scoreBreakdown: false,
  qualityInfo: false,
  dofInfo: false,
};

// Detection color coding - uses CSS variables from tokens.css
// These are fallback values; actual CSS will use var() in styles
const COLORS = {
  primary: '#ff6b6b',        // --color-danger
  interesting: '#ff9b64',    // --color-warning
  subject: '#8fbba8',        // --color-text-muted
  nonSubject: '#102420',     // --color-border
  cropBox: '#68ff9a',        // --color-accent
  focusZone: '#4da6ff',      // --color-info
  rollLevel: '#68ff9a',      // --color-accent (level)
  rollSlight: '#ff9b64',     // --color-warning (slight)
  rollSignificant: '#ff6b6b', // --color-danger (significant)
  pose: '#00ffff',           // cyan for pose skeleton
  poseLeft: '#ff6b6b',       // red for left side
  poseRight: '#4da6ff',      // blue for right side
};

// COCO keypoint connections for pose skeleton
const POSE_CONNECTIONS = [
  // Face
  [0, 1], [0, 2], [1, 3], [2, 4],
  // Upper body
  [5, 6], [5, 7], [7, 9], [6, 8], [8, 10],
  // Torso
  [5, 11], [6, 12], [11, 12],
  // Lower body
  [11, 13], [13, 15], [12, 14], [14, 16],
];

// Keypoint names for reference
const KEYPOINT_NAMES = [
  'nose', 'left_eye', 'right_eye', 'left_ear', 'right_ear',
  'left_shoulder', 'right_shoulder', 'left_elbow', 'right_elbow',
  'left_wrist', 'right_wrist', 'left_hip', 'right_hip',
  'left_knee', 'right_knee', 'left_ankle', 'right_ankle',
];

/**
 * Pose skeleton sub-component.
 */
const PoseSkeleton = React.memo(function PoseSkeleton({ keypoints, scaler, color = COLORS.pose }) {
  if (!keypoints || keypoints.length < 17) return null;

  // Filter to valid keypoints (confidence > 0.3)
  const validPoints = keypoints.map((kp, idx) => {
    if (!kp || kp.length < 3) return null;
    const [x, y, conf] = kp;
    if (conf < 0.3) return null;
    const scaled = scaler.point({ x, y });
    return { ...scaled, conf, idx };
  });

  return (
    <svg
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
    >
      {/* Draw connections */}
      {POSE_CONNECTIONS.map(([i, j], connIdx) => {
        const p1 = validPoints[i];
        const p2 = validPoints[j];
        if (!p1 || !p2) return null;

        // Color based on body side
        const isLeft = KEYPOINT_NAMES[i]?.includes('left') || KEYPOINT_NAMES[j]?.includes('left');
        const isRight = KEYPOINT_NAMES[i]?.includes('right') || KEYPOINT_NAMES[j]?.includes('right');
        const lineColor = isLeft ? COLORS.poseLeft : isRight ? COLORS.poseRight : color;

        return (
          <line
            key={`conn-${connIdx}`}
            x1={p1.x}
            y1={p1.y}
            x2={p2.x}
            y2={p2.y}
            stroke={lineColor}
            strokeWidth={2}
            opacity={0.8}
          />
        );
      })}

      {/* Draw keypoints */}
      {validPoints.map((p, idx) => {
        if (!p) return null;
        const isLeft = KEYPOINT_NAMES[idx]?.includes('left');
        const isRight = KEYPOINT_NAMES[idx]?.includes('right');
        const pointColor = isLeft ? COLORS.poseLeft : isRight ? COLORS.poseRight : color;

        return (
          <circle
            key={`kp-${idx}`}
            cx={p.x}
            cy={p.y}
            r={4}
            fill={pointColor}
            stroke="#000"
            strokeWidth={1}
            opacity={0.9}
          />
        );
      })}
    </svg>
  );
});

/**
 * Detection box sub-component.
 */
const DetectionBox = React.memo(function DetectionBox({
  detection,
  isPrimary,
  scaler,
  showLabel,
  showScoreBreakdown,
}) {
  const style = scaler.boxToPercent(detection.box);

  // Determine color based on status
  const color = isPrimary
    ? COLORS.primary
    : detection.is_interesting
      ? COLORS.interesting
      : detection.is_subject
        ? COLORS.subject
        : COLORS.nonSubject;

  const borderStyle = detection.is_subject ? 'solid' : 'dashed';
  const borderWidth = (() => {
    if (isPrimary) return 3;
    if (detection.is_interesting) return 2;
    return 1;
  })();

  const boxStyle = {
    position: 'absolute',
    ...style,
    border: `${borderWidth}px ${borderStyle} ${color}`,
    boxSizing: 'border-box',
    pointerEvents: 'none', // Don't interfere with face tagging overlay
    cursor: 'default',
  };

  // Build label parts
  const labelParts = [];

  // Basic info: id, class, confidence
  const classInfo = detection.class_name || 'unknown';
  const confInfo = detection.confidence ? `${(detection.confidence * 100).toFixed(0)}%` : '';
  labelParts.push(`${detection.id} ${classInfo} ${confInfo}`);

  // Role and importance
  if (detection.role) {
    labelParts.push(`${detection.role} | ${detection.importance_score?.toFixed(2) || '?'}`);
  }

  // Interesting flag
  if (detection.is_interesting) {
    labelParts.push('INTERESTING');
  } else if (detection.elimination_reason) {
    labelParts.push(`[${detection.elimination_reason}]`);
  }

  // Score breakdown for analysis mode
  const scoreBreakdownParts = [];
  if (showScoreBreakdown && detection.score_factors) {
    const sf = detection.score_factors;
    scoreBreakdownParts.push(
      `sz:${sf.size?.toFixed(2) || '?'} foc:${sf.focus?.toFixed(2) || '?'} pos:${sf.position?.toFixed(2) || '?'} role:${sf.role?.toFixed(2) || '?'}`
    );

    // Additional details
    if (detection.sharpness_laplace !== undefined) {
      scoreBreakdownParts.push(`sharp:${detection.sharpness_laplace.toFixed(0)}`);
    }
    if (detection.dof_weight !== undefined) {
      scoreBreakdownParts.push(`dof:${detection.dof_weight.toFixed(2)}`);
    }
    if (detection.facing_dir) {
      scoreBreakdownParts.push(`facing:${detection.facing_dir}`);
    }
    if (detection.looking_at_ball) {
      scoreBreakdownParts.push('AT_BALL');
    }
  }

  return (
    <div
      className="viz-detection-box"
      style={boxStyle}
    >
      {/* X mark for non-sharp detections */}
      {!detection.is_sharp_enough && (
        <svg
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
          }}
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <line
            x1="10"
            y1="10"
            x2="90"
            y2="90"
            stroke="#ff0000"
            strokeWidth="3"
            opacity="0.7"
          />
          <line
            x1="90"
            y1="10"
            x2="10"
            y2="90"
            stroke="#ff0000"
            strokeWidth="3"
            opacity="0.7"
          />
        </svg>
      )}

      {/* Detection label - top */}
      {showLabel && (
        <div
          style={{
            position: 'absolute',
            top: '-18px',
            left: 0,
            fontSize: '9px',
            fontFamily: 'monospace',
            backgroundColor: color,
            color: '#fff',
            padding: '1px 4px',
            whiteSpace: 'nowrap',
            lineHeight: 1.2,
          }}
        >
          {labelParts[0]}
        </div>
      )}

      {/* Secondary label - bottom (role/score/interesting) */}
      {showLabel && labelParts.length > 1 && (
        <div
          style={{
            position: 'absolute',
            bottom: '-18px',
            left: 0,
            fontSize: '9px',
            fontFamily: 'monospace',
            backgroundColor: color,
            color: '#fff',
            padding: '1px 4px',
            whiteSpace: 'nowrap',
            lineHeight: 1.2,
            opacity: 0.9,
          }}
        >
          {labelParts.slice(1).join(' | ')}
        </div>
      )}

      {/* Score breakdown - right side (analysis mode) */}
      {showScoreBreakdown && scoreBreakdownParts.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: '-4px',
            transform: 'translateX(100%)',
            fontSize: '8px',
            fontFamily: 'monospace',
            backgroundColor: 'rgba(0,0,0,0.8)',
            color: '#fff',
            padding: '2px 4px',
            whiteSpace: 'nowrap',
            lineHeight: 1.3,
          }}
        >
          {scoreBreakdownParts.map((part, i) => (
            <div key={i}>{part}</div>
          ))}
        </div>
      )}
    </div>
  );
});

/**
 * Crop box with rule-of-thirds grid.
 */
const CropBoxLayer = React.memo(function CropBoxLayer({ crop, scaler, showGrid }) {
  if (!crop?.box) return null;

  const style = scaler.boxToPercent(crop.box);

  return (
    <div
      className="viz-crop-box"
      style={{
        position: 'absolute',
        ...style,
        border: `2px solid ${COLORS.cropBox}`,
        boxShadow: `0 0 0 1px rgba(102, 255, 183, 0.35)`,
        pointerEvents: 'none',
        boxSizing: 'border-box',
      }}
    >
      {/* Rule of thirds grid */}
      {showGrid && (
        <>
          {/* Horizontal lines */}
          <div
            style={{
              position: 'absolute',
              top: '33.333%',
              left: 0,
              right: 0,
              height: '1px',
              backgroundColor: 'rgba(255,255,255,0.3)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: '66.666%',
              left: 0,
              right: 0,
              height: '1px',
              backgroundColor: 'rgba(255,255,255,0.3)',
            }}
          />
          {/* Vertical lines */}
          <div
            style={{
              position: 'absolute',
              left: '33.333%',
              top: 0,
              bottom: 0,
              width: '1px',
              backgroundColor: 'rgba(255,255,255,0.3)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              left: '66.666%',
              top: 0,
              bottom: 0,
              width: '1px',
              backgroundColor: 'rgba(255,255,255,0.3)',
            }}
          />
        </>
      )}

      {/* Crop method label */}
      <div
        style={{
          position: 'absolute',
          bottom: '-18px',
          left: 0,
          fontSize: '9px',
          fontFamily: 'monospace',
          backgroundColor: COLORS.cropBox,
          color: '#000',
          padding: '1px 4px',
          whiteSpace: 'nowrap',
        }}
      >
        {crop.reason || crop.method} | {crop.score?.toFixed(2) || '?'}
        {crop.breakdown && (
          <> | v={crop.breakdown.violations?.toFixed(0)} inc={crop.breakdown.relevant_included?.toFixed(0)}</>
        )}
      </div>
    </div>
  );
});

/**
 * Roll/tilt indicator showing detected horizon.
 */
function RollIndicator({ rollInfo, imgBounds }) {
  if (!rollInfo || (!rollInfo.applied && rollInfo.confidence < 0.3)) {
    return null;
  }

  const absAngle = Math.abs(rollInfo.angle_deg);
  const color =
    absAngle < 0.5
      ? COLORS.rollLevel
      : absAngle < 2.0
        ? COLORS.rollSlight
        : COLORS.rollSignificant;

  // Calculate line endpoints through center
  const centerX = imgBounds.w / 2;
  const centerY = imgBounds.h / 2;
  const length = Math.max(imgBounds.w, imgBounds.h);
  const radians = (rollInfo.angle_deg * Math.PI) / 180;

  const x1 = centerX - (length / 2) * Math.cos(radians);
  const y1 = centerY - (length / 2) * Math.sin(radians);
  const x2 = centerX + (length / 2) * Math.cos(radians);
  const y2 = centerY + (length / 2) * Math.sin(radians);

  return (
    <svg
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
    >
      <line
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke={color}
        strokeWidth={2}
        strokeDasharray={rollInfo.applied ? 'none' : '5,5'}
        opacity={0.8}
      />
      <text
        x={centerX + 10}
        y={centerY - 10}
        fill={color}
        fontSize="11"
        fontFamily="monospace"
      >
        TILT {rollInfo.angle_deg >= 0 ? '+' : ''}{rollInfo.angle_deg.toFixed(1)}deg (c={rollInfo.confidence?.toFixed(2) ?? '?'}){rollInfo.applied ? ' [APPLIED]' : ''}
      </text>
    </svg>
  );
}

/**
 * Focus zone and AF point crosshair with optional DOF info.
 */
function FocusZoneLayer({ focusInfo, scaler, imgBounds, showDofInfo }) {
  if (!focusInfo?.available) return null;

  // Build DOF info string
  const dofParts = [];
  if (showDofInfo) {
    if (focusInfo.lens_mm) {
      dofParts.push(`${focusInfo.lens_mm}mm`);
    }
    if (focusInfo.aperture) {
      dofParts.push(`f/${focusInfo.aperture}`);
    }
    if (focusInfo.dof_near_m != null && focusInfo.dof_far_m != null) {
      const near = focusInfo.dof_near_m.toFixed(1);
      const far = focusInfo.dof_far_m === Infinity ? 'inf' : focusInfo.dof_far_m.toFixed(1);
      dofParts.push(`DOF: ${near}m - ${far}m`);
    }
    if (focusInfo.focus_distance_m != null) {
      dofParts.push(`Focus: ${focusInfo.focus_distance_m.toFixed(1)}m`);
    }
  }

  return (
    <div style={{ pointerEvents: 'none' }}>
      {/* Focus zone box */}
      {focusInfo.zone_box && (
        <div
          style={{
            position: 'absolute',
            ...scaler.boxToPercent(focusInfo.zone_box),
            border: `1px dashed ${COLORS.focusZone}`,
            backgroundColor: 'rgba(0, 255, 255, 0.1)',
            boxSizing: 'border-box',
          }}
        />
      )}

      {/* AF point crosshair - note: point is normalized 0-1 */}
      {focusInfo.point && (
        <svg
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
          }}
        >
          <line
            x1={focusInfo.point.x * imgBounds.w - 20}
            y1={focusInfo.point.y * imgBounds.h}
            x2={focusInfo.point.x * imgBounds.w + 20}
            y2={focusInfo.point.y * imgBounds.h}
            stroke={COLORS.focusZone}
            strokeWidth={2}
          />
          <line
            x1={focusInfo.point.x * imgBounds.w}
            y1={focusInfo.point.y * imgBounds.h - 20}
            x2={focusInfo.point.x * imgBounds.w}
            y2={focusInfo.point.y * imgBounds.h + 20}
            stroke={COLORS.focusZone}
            strokeWidth={2}
          />
          <circle
            cx={focusInfo.point.x * imgBounds.w}
            cy={focusInfo.point.y * imgBounds.h}
            r={3}
            fill={COLORS.focusZone}
          />
        </svg>
      )}

      {/* DOF info panel */}
      {showDofInfo && dofParts.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            fontSize: '10px',
            fontFamily: 'monospace',
            backgroundColor: 'rgba(0,0,0,0.8)',
            color: COLORS.focusZone,
            padding: '4px 8px',
            borderRadius: '3px',
            lineHeight: 1.4,
          }}
        >
          {dofParts.map((part, i) => (
            <div key={i}>{part}</div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Canvas-based heatmap showing detection density.
 */
const HeatmapLayer = React.memo(function HeatmapLayer({ detections, imgBounds, scaler }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !detections?.length) return;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Create gradient points for each detection center
    detections.forEach((det) => {
      if (!det.box) return;

      const center = {
        x: (det.box.x1 + det.box.x2) / 2,
        y: (det.box.y1 + det.box.y2) / 2,
      };
      const scaledCenter = scaler.point(center);
      const radius = Math.max(50, (det.importance_score || 0.5) * 150);

      const gradient = ctx.createRadialGradient(
        scaledCenter.x,
        scaledCenter.y,
        0,
        scaledCenter.x,
        scaledCenter.y,
        radius
      );

      const alpha = (det.importance_score || 0.5) * 0.3;
      gradient.addColorStop(0, `rgba(255, 100, 0, ${alpha})`);
      gradient.addColorStop(1, 'rgba(255, 100, 0, 0)');

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    });
  }, [detections, imgBounds?.w, imgBounds?.h, scaler]);

  return (
    <canvas
      ref={canvasRef}
      width={imgBounds?.w || 0}
      height={imgBounds?.h || 0}
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        opacity: 0.5,
      }}
    />
  );
});

/**
 * DOF-weighted heatmap for focus mode.
 * Shows depth of field weight distribution.
 */
const DofHeatmapLayer = React.memo(function DofHeatmapLayer({ detections, imgBounds, scaler }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !detections?.length) return;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Create gradient points for each detection center based on DOF weight
    detections.forEach((det) => {
      if (!det.box || det.dof_weight === undefined) return;

      const center = {
        x: (det.box.x1 + det.box.x2) / 2,
        y: (det.box.y1 + det.box.y2) / 2,
      };
      const scaledCenter = scaler.point(center);
      const radius = Math.max(40, det.dof_weight * 120);

      const gradient = ctx.createRadialGradient(
        scaledCenter.x,
        scaledCenter.y,
        0,
        scaledCenter.x,
        scaledCenter.y,
        radius
      );

      // Use cyan/blue for DOF heatmap to distinguish from importance heatmap
      const alpha = det.dof_weight * 0.4;
      gradient.addColorStop(0, `rgba(0, 200, 255, ${alpha})`);
      gradient.addColorStop(1, 'rgba(0, 200, 255, 0)');

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    });
  }, [detections, imgBounds?.w, imgBounds?.h, scaler]);

  return (
    <canvas
      ref={canvasRef}
      width={imgBounds?.w || 0}
      height={imgBounds?.h || 0}
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        opacity: 0.5,
      }}
    />
  );
});

/**
 * Quality info panel showing quality issues.
 */
function QualityInfoPanel({ quality, processingTime }) {
  if (!quality && !processingTime) return null;

  const parts = [];

  if (quality) {
    if (quality.blur_score !== undefined) {
      parts.push(`Blur: ${quality.blur_score.toFixed(0)}`);
    }
    if (quality.exposure_score !== undefined) {
      parts.push(`Exposure: ${quality.exposure_score.toFixed(0)}`);
    }
    if (quality.composition_score !== undefined) {
      parts.push(`Composition: ${quality.composition_score.toFixed(2)}`);
    }
    if (quality.issues?.length > 0) {
      parts.push(`Issues: ${quality.issues.join(', ')}`);
    }
  }

  if (processingTime !== undefined) {
    parts.push(`Time: ${processingTime}ms`);
  }

  if (parts.length === 0) return null;

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 8,
        left: 8,
        fontSize: '10px',
        fontFamily: 'monospace',
        backgroundColor: 'rgba(0,0,0,0.8)',
        color: '#fff',
        padding: '4px 8px',
        borderRadius: '3px',
        lineHeight: 1.4,
        maxWidth: '300px',
      }}
    >
      {parts.map((part, i) => (
        <div key={i}>{part}</div>
      ))}
    </div>
  );
}

// Layer presets for quick switching (Alt+V cycles through these)
export const VIZ_PRESETS = {
  off: {
    detections: false,
    cropBox: false,
    rollIndicator: false,
    focusZone: false,
    heatmap: false,
    labels: false,
    pose: false,
    scoreBreakdown: false,
    qualityInfo: false,
    dofInfo: false,
  },
  basic: {
    detections: true,
    cropBox: true,
    rollIndicator: true,
    focusZone: false,
    heatmap: false,
    labels: true,
    pose: false,
    scoreBreakdown: false,
    qualityInfo: false,
    dofInfo: false,
  },
  focus: {
    detections: false,
    cropBox: false,
    rollIndicator: true,
    focusZone: true,
    heatmap: false,
    labels: false,
    pose: false,
    scoreBreakdown: false,
    qualityInfo: false,
    dofInfo: true,
  },
  analysis: {
    detections: true,
    cropBox: true,
    rollIndicator: true,
    focusZone: true,
    heatmap: true,
    labels: true,
    pose: true,
    scoreBreakdown: true,
    qualityInfo: true,
    dofInfo: true,
  },
};

export const VIZ_PRESET_ORDER = ['off', 'basic', 'focus', 'analysis'];

/**
 * Main VizOverlay component.
 *
 * Controlled component - layers state managed by parent (Edit.jsx).
 * Use VIZ_PRESETS for preset-based switching with Alt+V.
 */
function VizOverlay({
  metadata,
  imgBounds,
  layers = DEFAULT_LAYERS,
}) {
  // Create scaler for detection coordinates (always detect-space)
  // Uses actual image_dims from Sentinel (e.g., 1333x2000 for portrait)
  const detectScaler = useMemo(() => {
    if (!imgBounds || !metadata?.image_dims) return null;

    const dims = metadata.image_dims;

    return createCoordinateScaler(
      dims.width,
      dims.height,
      imgBounds.w,
      imgBounds.h
    );
  }, [imgBounds, metadata?.image_dims]);

  // Create scaler for crop coordinates (may be original-space after recrop)
  const cropScaler = useMemo(() => {
    if (!imgBounds || !metadata?.image_dims || !metadata?.crop) return null;

    const cropSpace = getCropCoordinateSpace(metadata.crop.method);

    if (cropSpace === 'original' && metadata.originalDims) {
      return createCoordinateScaler(
        metadata.originalDims.width,
        metadata.originalDims.height,
        imgBounds.w,
        imgBounds.h
      );
    }

    // Default to detect-space
    return detectScaler;
  }, [imgBounds, metadata, detectScaler]);

  // Get primary subject ID for highlighting
  const primaryId = metadata?.primary_subject?.detection?.id;

  // Early return if no data
  if (!metadata || !imgBounds || !detectScaler) {
    return null;
  }

  const detections = metadata.detections || [];

  return (
    <div
      className="viz-overlay"
      style={{
        position: 'absolute',
        // VizOverlay is rendered inside an overlay div that's already positioned at imgBounds.x/y
        // Use inset:0 to fill parent container, not imgBounds offset (which would double-offset)
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    >
      {/* DOF Heatmap layer for focus mode (behind everything) */}
      {layers.dofInfo && layers.focusZone && detections.length > 0 && (
        <DofHeatmapLayer
          detections={detections}
          imgBounds={imgBounds}
          scaler={detectScaler}
        />
      )}

      {/* Importance Heatmap layer (behind everything) */}
      {layers.heatmap && detections.length > 0 && (
        <HeatmapLayer
          detections={detections}
          imgBounds={imgBounds}
          scaler={detectScaler}
        />
      )}

      {/* Focus zone layer */}
      {layers.focusZone && metadata.focus_info && (
        <FocusZoneLayer
          focusInfo={metadata.focus_info}
          scaler={detectScaler}
          imgBounds={imgBounds}
          showDofInfo={layers.dofInfo}
        />
      )}

      {/* Roll indicator layer */}
      {layers.rollIndicator && metadata.roll_info && (
        <RollIndicator rollInfo={metadata.roll_info} imgBounds={imgBounds} />
      )}

      {/* Crop box layer */}
      {layers.cropBox && metadata.crop && cropScaler && (
        <CropBoxLayer
          crop={metadata.crop}
          scaler={cropScaler}
          showGrid={true}
        />
      )}

      {/* Pose skeletons layer (before detection boxes) */}
      {layers.pose &&
        detections.map((det) => {
          if (!det.keypoints || det.keypoints.length < 17) return null;
          return (
            <PoseSkeleton
              key={`pose-${det.id}`}
              keypoints={det.keypoints}
              scaler={detectScaler}
            />
          );
        })}

      {/* Detection boxes layer */}
      {layers.detections &&
        detections.map((det) => (
          <DetectionBox
            key={det.id}
            detection={det}
            isPrimary={det.id === primaryId}
            scaler={detectScaler}
            showLabel={layers.labels}
            showScoreBreakdown={layers.scoreBreakdown}
          />
        ))}

      {/* Quality info panel (analysis mode) */}
      {layers.qualityInfo && (
        <QualityInfoPanel
          quality={metadata.quality}
          processingTime={metadata.processing_time_ms}
        />
      )}
    </div>
  );
}

export default React.memo(VizOverlay);
